// navigation/actions.js

import { navigationInitialState } from "./navigationState";

// Centralized mappings
const TAB_URL_MAP = {
  drafts: "/home/drafts",
  submitted: "/home/submitted",
  submits: "/home/submitted",
  approvals: "/home/approvals",
  del_reporting: "/home/del_reporting",
  reporting: "/home/reporting",
  samplingEvents: "/home/sampling_events",
  calibrationLogs: "/home/calibration_logs",
  wellDataUpload: "/home/well_data_upload",
  admin: "/home/admin",
  nearMiss: "/home/near_miss",
  safetyAudit: "/home/safety_audit",
  incidentInvestigation: "/home/incident_investigation",
  safetyFormReporting: "/home/safety_form_reporting",
  sdsLookup: "/home/sds_lookup",
};

const URL_TAB_MAP = {
  "/home/drafts": "drafts",
  "/home/submitted": "submitted",
  "/home/submits": "submitted", // Legacy support
  "/home/approvals": "approvals",
  "/home/del_reporting": "del_reporting",
  "/home/reporting": "reporting",
  "/home/sampling_events": "samplingEvents",
  "/home/calibration_logs": "calibrationLogs",
  "/home/well_data_upload": "wellDataUpload",
  "/home/admin": "admin",
  "/home/near_miss": "nearMiss",
  "/home/safety_audit": "safetyAudit",
  "/home/incident_investigation": "incidentInvestigation",
  "/home/safety_form_reporting": "safetyFormReporting",
  "/home/sds_lookup": "sdsLookup",
};

const extractTabFromUrl = (pathname) => {
  const parts = pathname.split("/").filter(Boolean);
  if (parts.length >= 2 && parts[0] === "home") {
    const seg = parts[1];
    if (seg === "sampling_events") return "samplingEvents";
    if (seg === "calibration_logs") return "calibrationLogs";
    if (seg === "well_data_upload") return "wellDataUpload";
    if (seg === "submits") return "submitted"; // alias support
    if (seg === "near_miss") return "nearMiss";
    if (seg === "safety_audit") return "safetyAudit";
    if (seg === "incident_investigation") return "incidentInvestigation";
    if (seg === "safety_form_reporting") return "safetyFormReporting";
    if (seg === "sds_lookup") return "sdsLookup";
    // Known tabs
    const known = [
      "drafts",
      "submitted",
      "approvals",
      "del_reporting",
      "reporting",
      "admin",
    ];
    return known.includes(seg) ? seg : null;
  }
  return null;
};

export const makeNavigationActions = (set, get) => ({
  // Basic setters
  setActiveTab: (tab) =>
    set((s) => ({ navigation: { ...s.navigation, activeTab: tab } })),
  setUrl: (url) => set((s) => ({ navigation: { ...s.navigation, url } })),
  resetNavigation: () => set({ navigation: { ...navigationInitialState } }),

  // Navigate by tab name
  navigateToTab: (navigate, tabName, opts) => {
    const url = TAB_URL_MAP[tabName] || "/home/drafts";

    set((s) => ({
      navigation: { ...s.navigation, activeTab: tabName, url },
    }));

    navigate(url, { replace: !!opts?.replace });

    // Mobile submenu/cleanup (optional callbacks until UI slices are migrated)
    if (typeof window !== "undefined" && window.innerWidth < 1024) {
      opts?.onToggleDelMenu?.(); // e.g., close the DEL menu
      opts?.onToggleAdminMenu?.();
    }
    opts?.onHideDraftsDelForm?.();
    opts?.onHideSubmitsDelForm?.();
  },

  // Navigate by raw URL (reverse map to tab)
  navigateToUrl: (navigate, url, opts) => {
    const tabName = URL_TAB_MAP[url] || extractTabFromUrl(url);
    if (tabName && TAB_URL_MAP[tabName]) {
      set((s) => ({
        navigation: { ...s.navigation, activeTab: tabName, url },
      }));
    } else {
      set((s) => ({ navigation: { ...s.navigation, url } }));
    }
    navigate(url, { replace: !!opts?.replace });
  },

  // Sync state from the current location on load/refresh
  syncTabWithUrl: (pathname) => {
    const tabName = URL_TAB_MAP[pathname] || extractTabFromUrl(pathname);
    if (tabName && TAB_URL_MAP[tabName]) {
      set((s) => ({
        navigation: { ...s.navigation, activeTab: tabName, url: pathname },
      }));
    } else {
      set((s) => ({ navigation: { ...s.navigation, url: pathname } }));
    }
  },

  // Utility
  getUrlForTab: (tabName) => TAB_URL_MAP[tabName] || "/home",
});
